import { useMemo, useState } from 'react';
import Ics204HeaderWorkflowMenu from 'components/IAP/planningView/widgets/AssignmentList/Ics204HeaderWorkflowMenu';
import AssignmentCardView from 'components/IAP/planningView/widgets/AssignmentList/AssignmentCardView';
import type { AssignmentListRow } from 'components/IAP/planningView/widgets/AssignmentList/AssignmentContainer';
import { mapProposedStatus } from 'components/IAP/planningView/widgets/AssignmentList/assignmentStatus';
import { useSectionChiefResolver } from 'components/IAP/planningView/widgets/AssignmentList/useSectionChiefResolver';
import {
  isVersionLockedBySignatures,
  parseWalaSignatures,
} from 'components/IAP/planningView/widgets/AssignmentList/ics204SignatureSlots';
import type { Ics204EditingSection } from 'components/IAP/planningView/widgets/AssignmentList/ics204Sections';
import type {
  PlannedResource,
  WalaVersion,
  WorkAssignmentListAttachmentData,
} from 'components/IAP/hooks/assignmentListHooks';

function toAssignmentRow(
  attachment: WorkAssignmentListAttachmentData
): AssignmentListRow {
  return {
    id: attachment.id,
    walaId: attachment.walaId,
    workGroupId: attachment.workGroupId,
    workGroup: attachment.workGroupName,
    branch: attachment.branch,
    division: attachment.division,
    walaGroup: attachment.walaGroup,
    stagingArea: attachment.stagingArea,
    proposedStatus: mapProposedStatus(
      attachment.proposedStatus,
      attachment.status
    ),
    actualStatus: attachment.status,
    sectionChief: attachment.sectionChief,
    sectionChiefContact: attachment.sectionChiefContact,
    branchDirector: attachment.branchDirector,
    branchDirectorContact: attachment.branchDirectorContact,
    divisionGroupSupervisor: attachment.divisionGroupSupervisor,
    divisionGroupSupervisorContact: attachment.divisionGroupSupervisorContact,
    workAssignments: [],
    workGroupResourceNeeds: [],
    plannedResources: attachment.plannedResources,
    medicalCommunication: attachment.medicalCommunication,
    evacuationCommunication: attachment.evacuationCommunication,
    otherCommunication: attachment.otherCommunication,
    specialInstructions: attachment.specialInstructions,
    signatures: attachment.signatures,
    updatedAt: attachment.updatedAt,
    updatedByUserName: attachment.updatedByUserName,
  };
}

/**
 * One ICS 204, with the same approve and deny menu the assignment list uses.
 */
export default function Ics204ReviewDialog({
  attachment,
}: {
  attachment: WorkAssignmentListAttachmentData;
}) {
  const item = useMemo(() => toAssignmentRow(attachment), [attachment]);
  const { isCurrentUserSectionChief: isCurrentUserReviewer, sectionChief } =
    useSectionChiefResolver(item.workGroupId);
  const [editingSection, setEditingSection] =
    useState<Ics204EditingSection | null>(null);
  const [isCreatingSignedVersion, setIsCreatingSignedVersion] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<WalaVersion | null>(
    null
  );

  const tipSignatures = parseWalaSignatures(item.signatures);
  const signatureLocked = isVersionLockedBySignatures(tipSignatures);
  const contentLocked =
    !!viewingVersion || (signatureLocked && !isCreatingSignedVersion);

  const displayItem = useMemo((): AssignmentListRow => {
    if (!viewingVersion) return item;
    return {
      ...item,
      branch: viewingVersion.branch ?? '',
      division: viewingVersion.division ?? '',
      walaGroup: viewingVersion.walaGroup ?? '',
      stagingArea: viewingVersion.stagingArea ?? '',
      sectionChief: viewingVersion.sectionChief ?? undefined,
      sectionChiefContact: viewingVersion.sectionChiefContact ?? undefined,
      branchDirector: viewingVersion.branchDirector ?? undefined,
      branchDirectorContact: viewingVersion.branchDirectorContact ?? undefined,
      divisionGroupSupervisor:
        viewingVersion.divisionGroupSupervisor ?? undefined,
      divisionGroupSupervisorContact:
        viewingVersion.divisionGroupSupervisorContact ?? undefined,
      medicalCommunication: viewingVersion.medicalCommunication ?? undefined,
      evacuationCommunication:
        viewingVersion.evacuationCommunication ?? undefined,
      otherCommunication: viewingVersion.otherCommunication ?? undefined,
      specialInstructions: viewingVersion.specialInstructions ?? undefined,
      plannedResources:
        (viewingVersion.resourceSnapshot as PlannedResource[] | null) ??
        item.plannedResources,
      signatures: parseWalaSignatures(viewingVersion.signatures),
    };
  }, [item, viewingVersion]);

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <div className="tw:flex tw:justify-end">
        <Ics204HeaderWorkflowMenu
          walaId={item.walaId}
          actualStatus={item.actualStatus}
          isCurrentUserReviewer={isCurrentUserReviewer}
          disabled={!!viewingVersion}
        />
      </div>
      <AssignmentCardView
        sectionChief={sectionChief}
        item={displayItem}
        tipItem={item}
        periodNumber={attachment.operationalPeriodNumber}
        contentLocked={contentLocked}
        editingSection={editingSection}
        onEditSection={(section) => {
          setViewingVersion(null);
          setEditingSection(section);
        }}
        onCancelSectionEdit={() => setEditingSection(null)}
        isCreatingSignedVersion={isCreatingSignedVersion}
        viewingVersionId={viewingVersion?.id ?? null}
        onStartCreateSignedVersion={() => {
          setViewingVersion(null);
          setEditingSection(null);
          setIsCreatingSignedVersion(true);
        }}
        onCancelCreateSignedVersion={() => setIsCreatingSignedVersion(false)}
        onSignedVersionCreated={() => setIsCreatingSignedVersion(false)}
        onUnsignedVersionCreated={() => setIsCreatingSignedVersion(false)}
        onViewVersion={(version) => {
          setIsCreatingSignedVersion(false);
          setEditingSection(null);
          setViewingVersion(version as WalaVersion);
        }}
        onViewLatest={() => setViewingVersion(null)}
      />
    </div>
  );
}
